import type { Channel, EntityBundle, Emoji, Profile } from '../core/types';

export interface EntitySnapshotMaps {
  readonly channel?: ReadonlyMap<string, unknown | null>;
  readonly emoji?: ReadonlyMap<string, unknown | null>;
  readonly profile?: ReadonlyMap<string, unknown | null>;
  readonly workspace_id?: string | null;
}

/** Maps Echo's app-wide entity snapshot context into the core feed entity bundle. */
export function entityBundleFromSnapshots(snapshots: EntitySnapshotMaps | null | undefined): EntityBundle {
  return {
    profiles: mapSnapshots(snapshots?.profile, profileFromSnapshot),
    channels: mapSnapshots(snapshots?.channel, channelFromSnapshot),
    emoji: mapSnapshots(snapshots?.emoji, emojiFromSnapshot)
  };
}

function mapSnapshots<T>(snapshots: ReadonlyMap<string, unknown | null> | undefined, map: (snapshot: unknown) => T | null): T[] {
  if (!snapshots) return [];

  const records: T[] = [];
  snapshots.forEach((snapshot) => {
    const record = map(snapshot);
    if (record) records.push(record);
  });
  return records;
}

function profileFromSnapshot(snapshot: unknown): Profile | null {
  if (!isRecord(snapshot)) return null;
  const id = stringField(snapshot, 'id');
  if (!id) return null;

  return {
    id,
    name: stringField(snapshot, 'full_name') ?? 'Unknown',
    accent: stringField(snapshot, 'tag_color') ?? '#64748b'
  };
}

function channelFromSnapshot(snapshot: unknown): Channel | null {
  if (!isRecord(snapshot)) return null;
  const id = stringField(snapshot, 'id');
  if (!id) return null;

  return {
    id,
    name: stringField(snapshot, 'name') ?? 'Untitled'
  };
}

function emojiFromSnapshot(snapshot: unknown): Emoji | null {
  if (!isRecord(snapshot)) return null;
  const id = stringField(snapshot, 'id');
  if (!id) return null;
  const shortcode = stringField(snapshot, 'shortcode') ?? id;

  return {
    id,
    shortcode,
    glyph: stringField(snapshot, 'native') ?? `:${shortcode}:`
  };
}

function stringField(record: Record<string, unknown>, key: string): string | undefined {
  const value = record[key];
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}
